import React from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import './GroupConfigurationSection.scss';

interface GroupConfigurationSectionProps {
  groupName: string;
  setGroupName: (name: string) => void;
  minSelections: number;
  setMinSelections: (min: number) => void;
  maxSelections: number;
  setMaxSelections: (max: number) => void;
  minVariance: number;
  setMinVariance: (min: number) => void;
  maxVariance: number;
  setMaxVariance: (max: number) => void;
  itemsCount: number;
}

const GroupConfigurationSection: React.FC<GroupConfigurationSectionProps> = ({
  groupName,
  setGroupName,
  minSelections,
  setMinSelections,
  maxSelections,
  setMaxSelections,
  minVariance,
  setMinVariance,
  maxVariance,
  setMaxVariance,
  itemsCount
}) => {
  // Increment/Decrement handlers with enhanced validation
  const handleIncrement = (type: 'minVariance' | 'maxVariance' | 'minSelections' | 'maxSelections') => {
    switch (type) {
      case 'minVariance':
        // Variance min can never exceed selections max or variance max
        if (minVariance < maxVariance && minVariance < maxSelections) {
          setMinVariance(minVariance + 1);
        }
        break;
      case 'maxVariance':
        // Variance max can never exceed selections max
        if (maxVariance < maxSelections) {
          setMaxVariance(maxVariance + 1);
        }
        break;
      case 'minSelections':
        if (minSelections < maxSelections) {
          setMinSelections(minSelections + 1);
        }
        break;
      case 'maxSelections':
        setMaxSelections(maxSelections + 1);
        break;
    }
  };

  const handleDecrement = (type: 'minVariance' | 'maxVariance' | 'minSelections' | 'maxSelections') => {
    switch (type) {
      case 'minVariance':
        if (minVariance > 0) {
          setMinVariance(minVariance - 1);
        }
        break;
      case 'maxVariance':
        if (maxVariance > minVariance) {
          setMaxVariance(maxVariance - 1);
        }
        break;
      case 'minSelections':
        if (minSelections > 0) {
          setMinSelections(minSelections - 1);
        }
        break;
      case 'maxSelections':
        if (maxSelections > minSelections) {
          // When reducing max selections, also adjust variance if needed
          const newMaxSelections = maxSelections - 1;
          setMaxSelections(newMaxSelections);
          
          // Ensure variance doesn't exceed new selection limits
          if (maxVariance > newMaxSelections) {
            setMaxVariance(newMaxSelections);
          }
          if (minVariance > newMaxSelections) {
            setMinVariance(Math.min(minVariance, newMaxSelections));
          }
        }
        break;
    }
  };

  // Intelligent dynamic configuration explanation with practical examples
  const generateConfigurationExplanation = () => {
    let explanation = "Con esta configuración, ";
    
    // Selection rules
    if (minSelections === 0 && maxSelections === 1) {
      explanation += "elegir para tu cliente será opcional, y podría elegir hasta 1 producto";
    } else if (minSelections === 0) {
      explanation += `elegir para tu cliente será opcional, y podría elegir hasta ${maxSelections} producto${maxSelections !== 1 ? 's' : ''}`;
    } else if (minSelections === maxSelections) {
      explanation += `tu cliente tendrá que elegir exactamente ${minSelections} producto${minSelections !== 1 ? 's' : ''}`;
    } else {
      explanation += `tu cliente tendrá que elegir como mínimo ${minSelections} producto${minSelections !== 1 ? 's' : ''}, y podría elegir hasta ${maxSelections} producto${maxSelections !== 1 ? 's' : ''}`;
    }
    
    explanation += ". ";
    
    // Variance rules (0 and 1 are functionally the same - no restriction)
    if (minVariance <= 1 && maxVariance <= 1) {
      explanation += "Puede elegir productos repetidos sin restricción.";
    } else if (minVariance === maxVariance) {
      explanation += `Además, tendría que elegir exactamente ${maxVariance} productos diferentes (no puede repetir).`;
    } else if (minVariance <= 1) {
      explanation += `Además, podría elegir hasta ${maxVariance} productos diferentes como máximo.`;
    } else {
      explanation += `Además, tendría que elegir como mínimo ${minVariance} productos diferentes, hasta ${maxVariance} como máximo.`;
    }

    // Add practical examples based on group name
    const groupNameLower = groupName.toLowerCase();
    let example = "";
    
    if (groupNameLower.includes('tamaño') || groupNameLower.includes('size')) {
      example = " (Ej: Pequeño, Mediano, Grande)";
    } else if (groupNameLower.includes('proteína') || groupNameLower.includes('protein')) {
      example = " (Ej: Pollo, Carne, Pescado)";
    } else if (groupNameLower.includes('acompañamiento') || groupNameLower.includes('acomp')) {
      example = " (Ej: Papas, Ensalada, Arroz)";
    } else if (groupNameLower.includes('salsa')) {
      example = " (Ej: Picante, Suave, Sin salsa)";
    } else if (groupNameLower.includes('bebida')) {
      example = " (Ej: Agua, Gaseosa, Jugo)";
    }
    
    return explanation + example;
  };

  // Number input with side arrows component
  const NumberInputWithSideArrows = ({ 
    value, 
    onIncrement, 
    onDecrement, 
    label, 
    min = 0,
    canIncrement = true,
    canDecrement = true 
  }: {
    value: number;
    onIncrement: () => void;
    onDecrement: () => void;
    label: string;
    min?: number;
    canIncrement?: boolean;
    canDecrement?: boolean;
  }) => (
    <div className="number-input-with-side-arrows">
      <Form.Label className="clean-label">{label}</Form.Label>
      <div className="input-with-side-controls">
        <button
          type="button"
          className="side-control-btn decrement"
          onClick={onDecrement}
          disabled={!canDecrement}
        >
          <FaChevronDown />
        </button>
        <Form.Control
          type="number"
          value={value}
          onChange={(e) => {
            // Direct input validation handled by increment/decrement logic
            const newValue = Math.max(min, Number(e.target.value) || min);
          }}
          className="clean-number-input"
          min={min}
        />
        <button
          type="button"
          className="side-control-btn increment"
          onClick={onIncrement}
          disabled={!canIncrement}
        >
          <FaChevronUp />
        </button>
      </div>
    </div>
  );

  return (
    <div className="group-config-section-clean">
      {/* Group Name */}
      <div className="group-name-section">
        <Form.Group>
          <Form.Label className="clean-label required">
            Nombre del Grupo
          </Form.Label>
          <Form.Control
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="Ej: Tamaños, Proteínas, Acompañamientos"
            className="clean-input"
          />
        </Form.Group>
      </div>

      {/* Configuration Grid */}
      <Row className="configuration-grid">
        <Col md={3}>
          <NumberInputWithSideArrows
            value={minVariance}
            onIncrement={() => handleIncrement('minVariance')}
            onDecrement={() => handleDecrement('minVariance')}
            label="Varianza Mín"
            min={0}
            canIncrement={minVariance < maxVariance && minVariance < maxSelections}
            canDecrement={minVariance > 0}
          />
        </Col>
        
        <Col md={3}>
          <NumberInputWithSideArrows
            value={maxVariance}
            onIncrement={() => handleIncrement('maxVariance')}
            onDecrement={() => handleDecrement('maxVariance')}
            label="Varianza Máx"
            min={0}
            canIncrement={maxVariance < maxSelections}
            canDecrement={maxVariance > minVariance}
          />
        </Col>

        <Col md={3}>
          <NumberInputWithSideArrows
            value={minSelections}
            onIncrement={() => handleIncrement('minSelections')}
            onDecrement={() => handleDecrement('minSelections')}
            label="Selecciones Mín"
            min={0}
            canIncrement={minSelections < maxSelections}
            canDecrement={minSelections > 0}
          />
        </Col>
        
        <Col md={3}>
          <NumberInputWithSideArrows
            value={maxSelections}
            onIncrement={() => handleIncrement('maxSelections')}
            onDecrement={() => handleDecrement('maxSelections')}
            label="Selecciones Máx"
            min={1}
            canDecrement={maxSelections > minSelections}
          />
        </Col>
      </Row>

      {/* Clean Configuration Banner */}
      <div className="configuration-banner-clean">
        <FaInfoCircle className="banner-icon-clean" />
        <span className="banner-text-clean">{generateConfigurationExplanation()}</span>
      </div>
    </div>
  );
};

export default GroupConfigurationSection;
